import { getSharingLinkInfo } from './getSharingLinkInfo';
import { assertNever } from 'owa-assert';
import { isFeatureEnabled } from 'owa-feature-flags';
import { HttpStatusCode, isSuccessStatusCode } from 'owa-http-status-codes';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import GetSharingInfoRequest from 'owa-service/lib/contract/GetSharingInfoRequest';
import GetSharingInfoResponse from 'owa-service/lib/contract/GetSharingInfoResponse';
import SharingLinkKind from 'owa-service/lib/contract/SharingLinkKind';
import getSharingInfoRequest from 'owa-service/lib/factory/getSharingInfoRequest';
import getSharingInfoResponse from 'owa-service/lib/factory/getSharingInfoResponse';
import getSharingInformationOperation from 'owa-service/lib/operation/getSharingInformationOperation';
import { trace } from 'owa-trace';
import {
    SharingLinkType,
    SharingLinkProviderType,
    SharingLinkInfoResponse,
    OneDriveProSharingLinkInfoResponse,
} from '../types/SharingLinkInfoResponse';

function createRequest(attachmentUrl: string): GetSharingInfoRequest {
    return getSharingInfoRequest({ attachmentUrl: attachmentUrl });
}

export function getSharingInformation(attachmentUrl: string): Promise<GetSharingInfoResponse> {
    if (isFeatureEnabled('doc-attachment-sharingLinkInfoOWSPrimeService')) {
        return getSharingInfoFromOWSPrime(attachmentUrl);
    }
    return getSharingInformationOperation(createRequest(attachmentUrl));
}

async function getSharingInfoFromOWSPrime(attachmentUrl: string): Promise<GetSharingInfoResponse> {
    const rawResponse = await getSharingLinkInfo(attachmentUrl, true);

    if (isSuccessStatusCode(rawResponse.status)) {
        const response: SharingLinkInfoResponse = JSON.parse(await rawResponse.text());

        if (isOneDriveProSharingLinkInfoResponse(response)) {
            const [anonymousEditLink] = response.sharingLinks.filter(
                sl => sl.type === SharingLinkType.AnonymousEdit
            );
            const [anonymousViewLink] = response.sharingLinks.filter(
                sl => sl.type === SharingLinkType.AnonymousView
            );
            const [organizationEditLink] = response.sharingLinks.filter(
                sl => sl.type === SharingLinkType.OrganizationEdit
            );
            const [organizationViewLink] = response.sharingLinks.filter(
                sl => sl.type === SharingLinkType.OrganizationView
            );

            return getSharingInfoResponse({
                AccessRequestMode: response.accessRequestMode,
                AnonymousEditLink: anonymousEditLink ? anonymousEditLink.url : undefined,
                AnonymousViewLink: anonymousViewLink ? anonymousViewLink.url : undefined,
                CanCreateAnonymousEditLink: response.canCreateAnonymousEditLink,
                CanCreateAnonymousViewLink: response.canCreateAnonymousViewLink,
                CanCreateOrganizationEditLink: response.canCreateOrganizationEditLink,
                CanCreateOrganizationViewLink: response.canCreateOrganizationViewLink,
                CanShareExternally: response.canShareExternally,
                CanShareInternally: response.canShareInternally,
                CanonicalUrl: response.canonicalUrl,
                ExpirationDate: response.expirationTime,
                FileName: response.name,
                IsAnonymousEditLinkAvailable: !!anonymousEditLink,
                IsAnonymousViewLinkAvailable: !!anonymousViewLink,
                IsOrganizationEditLinkAvailable: !!organizationEditLink,
                IsOrganizationViewLinkAvailable: !!organizationViewLink,
                MimeType: undefined,
                OrganizationEditLink: organizationEditLink ? organizationEditLink.url : undefined,
                OrganizationViewLink: organizationViewLink ? organizationViewLink.url : undefined,
                ResultCode: AttachmentResultCode.Success,
                SharingLinkKind: convertSharingLinkTypeToSharingLinkKind(response.linkType),
            });
        }

        throw new Error('Invalid response from OWS` from sharing link info');
    } else if (rawResponse.status === HttpStatusCode.NotFound) {
        return getSharingInfoResponse({
            ResultCode: AttachmentResultCode.NotFound,
        });
    } else if (rawResponse.status === 400) {
        return getSharingInfoResponse({
            ResultCode: AttachmentResultCode.InvalidUrl,
        });
    } else {
        trace.warn(`Invalid response for OWS' get share info. Status code: ${rawResponse.status}`);
        return getSharingInfoResponse({
            ResultCode: AttachmentResultCode.GenericFailure,
        });
    }
}

function isOneDriveProSharingLinkInfoResponse(
    response: SharingLinkInfoResponse
): response is OneDriveProSharingLinkInfoResponse {
    return response.providerType === SharingLinkProviderType.OneDrivePro;
}

function convertSharingLinkTypeToSharingLinkKind(type: SharingLinkType): number {
    switch (type) {
        case SharingLinkType.None:
            return SharingLinkKind.Uninitialized;
        case SharingLinkType.View:
            return SharingLinkKind.Direct;
        case SharingLinkType.Edit:
            return SharingLinkKind.Direct;
        case SharingLinkType.AnonymousView:
            return SharingLinkKind.AnonymousView;
        case SharingLinkType.AnonymousEdit:
            return SharingLinkKind.AnonymousEdit;
        case SharingLinkType.OrganizationView:
            return SharingLinkKind.OrganizationView;
        case SharingLinkType.OrganizationEdit:
            return SharingLinkKind.OrganizationEdit;
        default:
            return assertNever(type);
    }
}
